import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { StockLocation } from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import { formatCount } from "@shared-core/domain/formatters";
import { Button } from "../../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import { Label } from "../../app/components/primitives/label";
import { selectClassName } from "../stock/StockFormField";

/** Staging only: imports the customer's stock balances from Tharstern's REFRESH copy and starts reconciling them. */
export function StartShadowDialog({
  customerId,
  customerName,
  tharsternCustomerCode,
  onClose,
  onStarted,
}: {
  customerId: string;
  customerName: string | null;
  tharsternCustomerCode: string | null;
  onClose: () => void;
  onStarted: (message: string) => void;
}) {
  const dataAdapter = useDataAdapter();
  const [locations, setLocations] = useState<StockLocation[]>([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .listStockLocations()
      .then((result) => !cancelled && setLocations(result))
      .catch((err: unknown) => {
        console.error("stock.locations.load_failed", {
          status: err instanceof ApiError ? err.status : undefined,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  async function handleStart() {
    setIsSaving(true);
    setError(null);
    try {
      const result = await dataAdapter.startStockShadow(
        customerId,
        warehouseId,
      );
      onStarted(
        `${customerName ?? tharsternCustomerCode ?? customerId}'s shadow started: ${formatCount(result.lines.imported)} balances imported, ${formatCount(result.lines.skipped)} skipped, ${formatCount(result.lines.refused)} refused.`,
      );
    } catch (err) {
      console.error("stock_cutover.shadow.start_failed", {
        status: err instanceof ApiError ? err.status : undefined,
      });
      setError(
        err instanceof ApiError && (err.status === 409 || err.status === 400)
          ? err.detail
          : "Couldn't start the shadow. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Start shadow</DialogTitle>
          <DialogDescription>
            {customerName ?? "This customer"}
            {tharsternCustomerCode && ` (${tharsternCustomerCode})`}. Starting
            will import the customer's stock balances from Tharstern's REFRESH
            copy into One on staging, then reconcile the balances every day.
            Nothing will be ordered.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              {error}
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="shadow-warehouse">Import into</Label>
            <select
              id="shadow-warehouse"
              className={selectClassName}
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              <option value="" disabled>
                Choose a warehouse
              </option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleStart()}
            disabled={isSaving || !warehouseId}
          >
            {isSaving ? "Starting…" : "Start shadow"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
